import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState } from 'react';
import styled from '@emotion/styled';
// Lucide React 직접 import - Vite 번들링 최적화 및 초기화 순서 문제 해결
import { CheckCircle } from 'lucide-react';
import { XCircle } from 'lucide-react';
import { AlertCircle } from 'lucide-react';
const Container = styled.div`
  background: var(--vscode-editor-background, var(--theme-paper));
  border: 1px solid var(--vscode-editorWidget-border, var(--theme-border));
  border-radius: var(--theme-radius-md, 8px);
  padding: var(--theme-spacing-lg, 24px);
  margin: var(--theme-spacing-lg, 24px) 0;
`;
const Question = styled.div`
  margin-bottom: var(--theme-spacing-lg, 24px);
`;
const QuestionText = styled.h4`
  margin: 0 0 var(--theme-spacing-md, 16px) 0;
  font-size: var(--theme-typography-font-size-base, 1rem);
  font-weight: var(--theme-typography-font-weight-medium, 500);
  color: var(--vscode-editor-foreground, var(--theme-text));
`;
const Options = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--theme-spacing-sm, 8px);
`;
const Option = styled.label`
  display: flex;
  align-items: center;
  padding: var(--theme-spacing-sm, 8px) var(--theme-spacing-md, 16px);
  border: 1px solid ${(props)=>props.correct ? 'var(--vscode-testing-iconPassed, #4CAF50)' : props.incorrect ? 'var(--vscode-testing-iconFailed, #F44336)' : 'var(--vscode-editorWidget-border, var(--theme-border))'};
  background: ${(props)=>props.correct ? 'rgba(76, 175, 80, 0.1)' : props.incorrect ? 'rgba(244, 67, 54, 0.1)' : props.selected ? 'var(--vscode-list-activeSelectionBackground, rgba(33, 150, 243, 0.1))' : 'transparent'};
  border-radius: var(--theme-radius-sm, 4px);
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: ${(props)=>props.correct || props.incorrect ? undefined : 'var(--vscode-list-hoverBackground, rgba(0, 0, 0, 0.04))'};
  }
  
  input {
    margin-right: var(--theme-spacing-sm, 8px);
  }
`;
const Explanation = styled.div`
  margin-top: var(--theme-spacing-md, 16px);
  padding: var(--theme-spacing-md, 16px);
  background: var(--vscode-editor-selectionBackground, rgba(33, 150, 243, 0.1));
  border-radius: var(--theme-radius-sm, 4px);
  font-size: var(--vscode-font-size, var(--theme-typography-font-size-sm, 0.875rem));
  color: var(--vscode-editor-foreground, var(--theme-text));
  display: flex;
  align-items: flex-start;
  gap: var(--theme-spacing-sm, 8px);
  
  svg {
    flex-shrink: 0;
    margin-top: 2px;
  }
`;
const QuizButton = styled.button`
  margin-top: var(--theme-spacing-md, 16px);
  padding: var(--theme-spacing-sm, 8px) var(--theme-spacing-lg, 24px);
  background: var(--vscode-button-background, var(--theme-primary));
  color: var(--vscode-button-foreground, white);
  border: none;
  border-radius: var(--theme-radius-sm, 4px);
  font-size: var(--theme-typography-font-size-sm, 0.875rem);
  font-weight: var(--theme-typography-font-weight-medium, 500);
  cursor: pointer;
  transition: opacity 0.2s;
  
  &:hover {
    opacity: 0.9;
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;
const InteractiveQuiz = ({ quizData, onSubmit })=>{
    const [answers, setAnswers] = useState({});
    const [submitted, setSubmitted] = useState(false);
    const [results, setResults] = useState({});
    const handleAnswer = (questionId, value, isMultiple)=>{
        if (submitted) return;
        if (isMultiple) {
            const current = answers[questionId] || [];
            const updated = current.includes(value) ? current.filter((v)=>v !== value) : [
                ...current,
                value
            ];
            setAnswers({
                ...answers,
                [questionId]: updated
            });
        } else {
            setAnswers({
                ...answers,
                [questionId]: value
            });
        }
    };
    const handleSubmit = ()=>{
        const newResults = {};
        quizData.forEach((q)=>{
            const userAnswer = answers[q.id];
            if (q.type === 'multiple-choice' && q.answerIndex !== undefined && q.options) {
                newResults[q.id] = userAnswer === q.options[q.answerIndex];
            } else if (q.type === 'multiple-answer' && Array.isArray(q.answer)) {
                const userAnswerArray = userAnswer || [];
                newResults[q.id] = userAnswerArray.length === q.answer.length && userAnswerArray.every((a)=>q.answer.includes(a));
            }
        });
        setResults(newResults);
        setSubmitted(true);
        onSubmit?.(answers, newResults);
    };
    return /*#__PURE__*/ _jsxs(Container, {
        children: [
            quizData.map((q, index)=>/*#__PURE__*/ _jsxs(Question, {
                    children: [
                        /*#__PURE__*/ _jsxs(QuestionText, {
                            children: [
                                index + 1,
                                ". ",
                                q.question
                            ]
                        }),
                        /*#__PURE__*/ _jsx(Options, {
                            children: q.options?.map((option, optionIndex)=>{
                                const isSelected = q.type === 'multiple-choice' ? answers[q.id] === option : (answers[q.id] || []).includes(option);
                                const isCorrect = submitted && (q.type === 'multiple-choice' && q.answerIndex === optionIndex || q.type === 'multiple-answer' && q.answer?.includes(option));
                                const isIncorrect = submitted && isSelected && !isCorrect;
                                return /*#__PURE__*/ _jsxs(Option, {
                                    selected: isSelected,
                                    correct: isCorrect,
                                    incorrect: isIncorrect,
                                    children: [
                                        /*#__PURE__*/ _jsx("input", {
                                            type: q.type === 'multiple-choice' ? 'radio' : 'checkbox',
                                            name: q.id,
                                            value: option,
                                            checked: isSelected,
                                            onChange: ()=>handleAnswer(q.id, option, q.type === 'multiple-answer'),
                                            disabled: submitted
                                        }),
                                        /*#__PURE__*/ _jsx("span", {
                                            children: option
                                        }),
                                        submitted && isCorrect && /*#__PURE__*/ _jsx(CheckCircle, {
                                            size: 16,
                                            style: {
                                                marginLeft: 'auto'
                                            }
                                        }),
                                        submitted && isIncorrect && /*#__PURE__*/ _jsx(XCircle, {
                                            size: 16,
                                            style: {
                                                marginLeft: 'auto'
                                            }
                                        })
                                    ]
                                }, optionIndex);
                            })
                        }),
                        submitted && q.explanation && /*#__PURE__*/ _jsxs(Explanation, {
                            children: [
                                /*#__PURE__*/ _jsx(AlertCircle, {
                                    size: 16
                                }),
                                /*#__PURE__*/ _jsx("span", {
                                    children: q.explanation
                                })
                            ]
                        })
                    ]
                }, q.id)),
            /*#__PURE__*/ _jsx(QuizButton, {
                onClick: handleSubmit,
                disabled: submitted,
                children: submitted ? '제출 완료' : '답안 제출'
            })
        ]
    });
};
// Default export for backward compatibility
export default InteractiveQuiz;
